/**
 * DmlChangeTablePicker — the "Change table" action on a DML target row
 * (UPDATE / DELETE / MERGE / INSERT full views), and — `variant="inline"` —
 * the table name in the compact popover's UPDATE / DELETE header, which is
 * itself the picker there. Opens the searchable table picker and
 * rewrites the statement's target in ONE structural edit
 * (`change-dml-target-table`); the alias after the name is kept. It replaced a
 * × that could only leave the statement without a target, behind a confirm
 * dialog.
 *
 * Lists the tables of the schema snapshot, grouped by schema like the starter
 * canvas picker, with the current target check-marked. Disabled — with the reason as tooltip — without an edit
 * context, before any table has loaded, or when the renderer says the target
 * is not a plain table this edit can rewrite (`blockedReason`).
 */

import { useMemo } from 'react';
import { ArrowLeftRight } from 'lucide-react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { DmlTargetKind } from '../../../../sql-resolver/types/structuralEdit';
import { lookupRelationTable } from '../../../../sql-resolver/service/snapshotRelations';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { changeDmlTarget } from '../../edit/edits/changeDmlTarget';
import { tableSuggestionKey } from '../../edit/edits/fromItemHelpers';
import { safeExtractStructure } from '../insert/insertColumnMapHelpers';
import { vqeCopy } from '../../ux/vqeCopy';
import { starterTableGroups } from '../../workspace/starterTableGroups';
import { CommandPickerPopover, type CommandPickerGroup } from './CommandPickerPopover';

export interface DmlChangeTablePickerProps {
  readonly target: DmlTargetKind;
  readonly editContext?: VqeEditContext;
  /** Why this statement's target can't be swapped here (alias / target list). */
  readonly blockedReason?: string;
  /**
   * `'button'` (default): the "⇄ Change table" action beside the target.
   * `'inline'`: the target name itself (`label`) is the trigger, styled by
   * `triggerClassName` — the compact header.
   */
  readonly variant?: 'button' | 'inline';
  readonly label?: string;
  readonly triggerClassName?: string;
}

export function DmlChangeTablePicker({
  target,
  editContext,
  blockedReason,
  variant = 'button',
  label,
  triggerClassName,
}: DmlChangeTablePickerProps) {
  const { track } = useAnalytics();
  const snapshot = editContext?.service.getSchemaSnapshot() ?? null;
  const starterGroups = useMemo(() => starterTableGroups(snapshot), [snapshot]);
  const targetItem = editContext ? safeExtractStructure(editContext)?.[`${target}Target` as const] : undefined;
  const current =
    targetItem?.target.kind === 'relation' ? lookupRelationTable(snapshot, targetItem.target.qualified) : undefined;
  const currentKey = current ? tableSuggestionKey(current) : null;

  const groups = useMemo<readonly CommandPickerGroup[]>(
    () =>
      starterGroups.map((group) => ({
        heading: group.heading,
        items: group.items.map((item) => ({
          key: item.key,
          value: item.value,
          label: item.label,
          checked: item.key === currentKey,
        })),
      })),
    [starterGroups, currentKey],
  );
  const tableByKey = useMemo(() => {
    const map = new Map<string, (typeof starterGroups)[number]['items'][number]['table']>();
    for (const group of starterGroups) for (const item of group.items) map.set(item.key, item.table);
    return map;
  }, [starterGroups]);

  const reason = !editContext
    ? vqeCopy.dmlTarget.disabled.notReady
    : blockedReason
      ? blockedReason
      : tableByKey.size === 0
        ? vqeCopy.dmlTarget.disabled.noTables
        : undefined;

  const handleSelect = (key: string): void => {
    const table = tableByKey.get(key);
    if (!table || !editContext || key === currentKey) return;
    // Outcome-dependent analytics, like the FROM change-table chooser: only an
    // edit that reached the buffer counts. `target` / `surface` are fixed tags.
    void changeDmlTarget(target, table, toCommitCtx(editContext)).then((outcome) => {
      if (outcome.status === 'applied') {
        track('feature:vqe_change_dml_target', { target, surface: variant === 'inline' ? 'compact' : 'full' });
      }
    });
  };

  if (variant === 'inline' && label !== undefined && reason !== undefined) {
    // Nothing to pick — the header keeps reading as plain text.
    return <span className={`truncate ${triggerClassName ?? ''}`}>{label}</span>;
  }

  return (
    <CommandPickerPopover
      testId={`vqe-${variant === 'inline' ? 'compact-' : ''}${target}-target-change`}
      triggerLabel={variant === 'inline' && label !== undefined ? label : vqeCopy.dmlTarget.changeTable}
      triggerVariant={variant === 'inline' ? 'inline' : 'ghost'}
      triggerClassName={triggerClassName}
      // The inline name truncates in a narrow header — the hint spells it out.
      hint={variant === 'inline' ? `${label} · ${vqeCopy.dmlTarget.changeTable}` : undefined}
      triggerIcon={ArrowLeftRight}
      groups={groups}
      disabled={reason !== undefined}
      tooltip={reason}
      onSelect={handleSelect}
      closeOnSelect
      // Schema-qualified table names run long: grow with them, within reason.
      contentClassName="w-auto min-w-72 max-w-96"
      searchPlaceholder={vqeCopy.dmlTarget.searchPlaceholder}
      emptyMessage={vqeCopy.dmlTarget.emptyMessage}
    />
  );
}
